import type { Address } from '@/api/address';

export interface ParsedAddress {
  street: string;
  houseNumberOrName: string;
  stateOrProvince: string;
  city: string;
  country?: string;
  postalCode: string;
}

// Constants
const PATTERNS = {
  STREET_NUMBER: /^(\d+)\s+(.+)/,
  CANADIAN_POSTAL: /[A-Z]\d[A-Z]\s?\d[A-Z]\d/i,
  PROVINCE: /\b[A-Z]{2}\b/,
} as const;

// Validation helpers
const validateRequiredField = (value: string | undefined, fieldName: string): void => {
  if (!value?.trim()) {
    throw new Error(`${fieldName} is required`);
  }
};

const validateAddress = (billingAddress?: string): void => {
  if (!billingAddress) {
    throw new Error('Billing address is required');
  }
  validateRequiredField(billingAddress, 'Street address');
  validateRequiredField(billingAddress, 'Postal code');
};

// Parsing helpers
const extractStreetComponents = (streetAddress: string) => {
  const match = streetAddress.match(PATTERNS.STREET_NUMBER);
  return {
    houseNumber: match?.[1] || '',
    street: match?.[2] || streetAddress,
  };
};

const splitAddressParts = (address: string): string[] => {
  return address.split(',').map((part) => part.trim());
};

// US Address Parser
const parseUSAddress = (billingAddress: string, country?: string, zipCode?: string): ParsedAddress => {
  const addressParts = splitAddressParts(billingAddress);

  if (addressParts.length < 2) {
    throw new Error('Address must include street and city (separated by commas)');
  }

  const { houseNumber, street } = extractStreetComponents(addressParts[0]);
  const city = addressParts[1];
  const state = addressParts[2]?.split(' ')[0] || '';

  validateRequiredField(street, 'Street name');
  validateRequiredField(city, 'City');

  return {
    street,
    houseNumberOrName: houseNumber,
    stateOrProvince: state,
    city,
    country,
    postalCode: zipCode || '',
  };
};

// Canadian Address Parser
const parseCanadianAddress = (address: string): ParsedAddress => {
  const postalCodeMatch = address.match(PATTERNS.CANADIAN_POSTAL);
  const postalCode = postalCodeMatch?.[0] || '';

  const withoutPostal = address.replace(PATTERNS.CANADIAN_POSTAL, '').trim();
  const provinceMatch = withoutPostal.match(PATTERNS.PROVINCE);
  const province = provinceMatch?.[0] || '';

  const withoutProvince = withoutPostal.replace(PATTERNS.PROVINCE, '').trim();
  const parts = splitAddressParts(withoutProvince);

  const { houseNumber, street } = extractStreetComponents(parts[0]);
  const city = parts[1] || '';

  validateRequiredField(street, 'Street name');
  validateRequiredField(city, 'City');
  validateRequiredField(postalCode, 'Postal code');

  return {
    street,
    houseNumberOrName: houseNumber,
    city,
    stateOrProvince: province,
    postalCode,
    country: 'CA',
  };
};

export const parseAddress = (
  billingAddress?: string,
  zipCode?: string,
  country?: string
): ParsedAddress | undefined => {
  if (!billingAddress) {
    return undefined;
  }

  validateAddress(billingAddress);

  return country === 'CA' ? parseCanadianAddress(billingAddress) : parseUSAddress(billingAddress, country, zipCode);
};

// Address comes pre-parsed from the autocomplete API; we only need to
// split the street-line into house number + street name to match the
// PSP-facing ParsedAddress shape.
export const parseAddressObject = (address: Address, country?: string): ParsedAddress => {
  const { houseNumber, street } = extractStreetComponents(address.addressLine1 || '');

  validateRequiredField(street, 'Street name');
  validateRequiredField(address.city, 'City');
  validateRequiredField(address.zipCode, 'Postal code');

  return {
    street,
    houseNumberOrName: houseNumber,
    stateOrProvince: address.state || '',
    city: address.city,
    country,
    postalCode: address.zipCode,
  };
};
